import React, { useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SubscriptionBillingReview from "./SubscriptionBillingReview";
import { formatMoney } from "./subscriptionPricing";
import {
  subscriptionCardSx as paperBorderSx,
  headCellSx,
  bodyCellSx,
} from "./subscriptionStyles";

export default function SubscriptionBillingDrawer({
  open,
  onClose,
  subscription: sub,
  payments = [],
  audit = [],
  error: pricingError,
}) {
  const [historyTab, setHistoryTab] = useState(0);
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "subscription-billing-title",
        sx: {
          width: { xs: "100%", sm: 760 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        gap={2}
        sx={{ px: { xs: 2, sm: 3 }, py: 2, flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id='subscription-billing-title'
            variant='h6'
            fontWeight={700}
          >
            Billing details
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ overflowWrap: "anywhere" }}
          >
            {sub.organization?.name || sub.owner?.name || sub.planName} /{" "}
            {sub.owner?.email}
          </Typography>
        </Box>
        <IconButton aria-label='Close billing details' onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: { xs: 2, sm: 3 },
          "& > *": { flexShrink: 0 },
        }}
      >
        <SubscriptionBillingReview subscription={sub} error={pricingError} />
        <Paper
          elevation={0}
          sx={{
            ...paperBorderSx,
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Typography variant='subtitle1' fontWeight={700}>
            History & Activity
          </Typography>

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 0.5, mb: 2 }}
          >
            Review payment records and subscription changes.
          </Typography>

          <Tabs
            value={historyTab}
            onChange={(_, value) => setHistoryTab(value)}
            variant='scrollable'
            scrollButtons='auto'
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 42,
              "& .MuiTab-root": {
                minHeight: 42,
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab label={`Payments (${payments.length})`} />
            <Tab label={`Subscription Activity (${audit.length})`} />
          </Tabs>

          {historyTab === 0 && (
            <Box
              sx={{
                height: { xs: 360, sm: 420 },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", mb: 1.5, flexShrink: 0 }}
              >
                Showing {payments.length} most recent payment records (up to
                50).
              </Typography>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Table
                  size='small'
                  stickyHeader
                  sx={{ "& th": { bgcolor: "background.paper" } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Date & Time</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Details</TableCell>
                      <TableCell sx={headCellSx} align='right'>
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Box
                            sx={{
                              py: 5,
                              textAlign: "center",
                            }}
                          >
                            <ReceiptLongOutlinedIcon
                              sx={{
                                fontSize: 32,
                                color: "text.disabled",
                                mb: 1,
                              }}
                            />

                            <Typography variant='body2' fontWeight={600}>
                              No payment records found
                            </Typography>

                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Payment transactions will appear here when
                              available.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={bodyCellSx}>{p.at}</TableCell>

                          <TableCell sx={bodyCellSx}>
                            <Chip
                              size='small'
                              label={
                                {
                                  succeeded: "Paid",
                                  failed: "Failed",
                                  pending: "Pending",
                                  refunded: "Refunded",
                                }[p.outcome] ||
                                p.outcome ||
                                "Unknown"
                              }
                              color={
                                {
                                  succeeded: "success",
                                  failed: "error",
                                  pending: "warning",
                                  refunded: "info",
                                }[p.outcome] || "default"
                              }
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>

                          <TableCell sx={bodyCellSx}>
                            {p.reason || "—"}
                          </TableCell>

                          <TableCell
                            sx={{
                              ...bodyCellSx,
                              whiteSpace: "nowrap",
                              fontVariantNumeric: "tabular-nums",
                            }}
                            align='right'
                          >
                            {formatMoney(p.amount_cents, p.currency)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {historyTab === 1 && (
            <Box
              sx={{
                height: { xs: 360, sm: 420 },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: "block", mb: 1.5, flexShrink: 0 }}
              >
                Showing {audit.length} most recent subscription changes (up to
                50).
              </Typography>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Table
                  size='small'
                  stickyHeader
                  sx={{ "& th": { bgcolor: "background.paper" } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Date & Time</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Change Details</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {audit.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Box
                            sx={{
                              py: 5,
                              textAlign: "center",
                            }}
                          >
                            <HistoryOutlinedIcon
                              sx={{
                                fontSize: 32,
                                color: "text.disabled",
                                mb: 1,
                              }}
                            />

                            <Typography variant='body2' fontWeight={600}>
                              No subscription changes found
                            </Typography>

                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Changes to this subscription will appear here when
                              recorded.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      audit.map((a, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={bodyCellSx}>{a.at}</TableCell>

                          <TableCell sx={bodyCellSx}>
                            <Typography
                              variant='body2'
                              sx={{ textTransform: "capitalize" }}
                            >
                              {a.status || "Unknown"}
                            </Typography>
                          </TableCell>

                          <TableCell sx={bodyCellSx}>
                            {a.message || "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Stack>
    </Drawer>
  );
}
